/**
 * Utility functions for the CRM application
 */

// Currencies that display/enter with 2 decimals: MYR + other fiat + stablecoins
// (dollar-pegged, sub-cent precision is noise). Non-stable crypto keeps native
// precision (8 dp). Kept in sync with the formatter sets in Checkpoints.
const TWO_DP_FIAT = new Set(['MYR', 'SGD', 'EUR', 'GBP', 'JPY', 'AUD', 'CNY', 'HKD', 'IDR', 'THB']);
const TWO_DP_STABLE = new Set(['USDT', 'USDC', 'FDUSD', 'USD', 'BUSD', 'DAI', 'TUSD']);

/**
 * Malaysia / Asia-Kuala-Lumpur is the ONE timezone the whole site displays
 * in, regardless of the browser's own timezone. Fixed UTC+8, no DST.
 * Every date/time formatter and KL-input converter below is built on this
 * single constant — don't hardcode the string 'Asia/Kuala_Lumpur' elsewhere.
 */
export const MYT = 'Asia/Kuala_Lumpur';

/**
 * True when the app runs from a developer's machine (localhost / 127.0.0.1) —
 * where the API is a separate origin (:5002). In production the API and the
 * uploads are served from the page's own origin. The ONE definition: the API
 * client and resolveUploadUrl both read it.
 */
export const isLocalHost = () =>
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

const DEV_API_ORIGIN = 'http://localhost:5002';

/**
 * Turn a stored '/uploads/…' path into something an <img src> / <a href> can
 * load. Uploads are served from the site root (NOT under /api); in dev the
 * server is on :5002, a different origin. A missing value passes through. The
 * ONE resolver (audit D-20: nine copies, and the pool's cards used raw paths,
 * so their images were broken in dev).
 *
 * ONLY our own uploads resolve (security scan 2026-10-31 M6): anything that is
 * not an '/uploads/…' path of this site — an outside link someone typed into a
 * field — becomes '' so it can never be opened from a proof/receipt link.
 * Accepted as-is: local previews (blob:, data:image/…) and a value this
 * function already resolved (same origin, or the dev API origin).
 * @param {string} u - stored upload path
 * @returns {string} loadable URL, '' when it is not one of our uploads (the input itself when empty)
 */
export const resolveUploadUrl = (u) => {
  if (!u) return u;
  const s = String(u).trim();
  if (/^blob:/i.test(s) || /^data:image\/[a-z0-9.+-]+[;,]/i.test(s)) return s;
  let path = s;
  if (/^https?:\/\//i.test(s)) {
    let parsed;
    try { parsed = new URL(s); } catch (e) { return ''; }
    const ours = parsed.origin === window.location.origin || parsed.origin === DEV_API_ORIGIN;
    if (!ours) return '';
    path = parsed.pathname + parsed.search;
  }
  if (!path.startsWith('/uploads/') || path.includes('..')) return '';
  return isLocalHost() ? `${DEV_API_ORIGIN}${path}` : path;
};

/**
 * Max decimals allowed for a currency AMOUNT input: MYR/fiat/stablecoins → 2,
 * non-stable crypto (BTC, ETH…) → 8. Price/rate fields use 4 (pass 4 to
 * clampDecimals directly for those).
 */
export const maxInputDecimals = (currency) => {
  const c = (currency || 'MYR').toString().toUpperCase();
  return (TWO_DP_FIAT.has(c) || TWO_DP_STABLE.has(c)) ? 2 : 8;
};

/**
 * Clamp a numeric INPUT string to at most `maxDp` decimal places, preserving
 * what the user is mid-typing (keeps a trailing "."). Use in number-input
 * onChange handlers so users can't enter more precision than allowed:
 *   - MYR / fiat / stablecoin amounts → 2 dp (via maxInputDecimals)
 *   - price / rate fields             → 4 dp (pass 4)
 */
export const clampDecimals = (value, maxDp = 2) => {
  if (value === null || value === undefined) return value;
  const s = String(value);
  const dot = s.indexOf('.');
  if (dot === -1) return s;
  return s.slice(0, dot + 1 + Math.max(0, maxDp));
};

/**
 * AMOUNT INPUTS NEVER CARRY A SIGN (30 Oct 2026: a finance user typed -36,782.73
 * into a crypto debit and the ledger was corrupted). Direction comes ONLY from
 * the Debit/Credit (Sent/Received) choice; the amount is always > 0.
 *   - blockSignKeys       -> onKeyDown: refuses '-', '+', 'e', 'E'
 *   - sanitizeAmountInput -> onChange: keeps digits and the dot only
 *   - blockSignPaste      -> onPaste: refuses a paste that carries a sign / exponent
 *   - amountInputGuard    -> spread {...amountInputGuard} onto the <input>
 *   - isPositiveAmount    -> submit check; AMOUNT_MUST_BE_POSITIVE is the message
 */
export const blockSignKeys = (e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); };

export const sanitizeAmountInput = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).replace(/[^0-9.]/g, '');
};

export const blockSignPaste = (e) => {
  const text = (e.clipboardData && e.clipboardData.getData('text')) || '';
  if (/[-+eE]/.test(text)) e.preventDefault();
};

export const amountInputGuard = { onKeyDown: blockSignKeys, onPaste: blockSignPaste };

export const AMOUNT_MUST_BE_POSITIVE = 'Amount must be a positive number; choose Debit or Credit for the direction.';

export const isPositiveAmount = (value) => {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) && n > 0;
};

// One Intl formatter per currency code — building one per call is measurable in
// a long list, and every screen asks for the same two or three codes.
const currencyFormatters = new Map();
const currencyFormatter = (currency) => {
  const code = String(currency || 'MYR').toUpperCase();
  let f = currencyFormatters.get(code);
  if (!f) {
    f = new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    currencyFormatters.set(code, f);
  }
  return f;
};

// Read an amount off the wire: a number, a numeric string ('1251.09' — SQLite
// REALs often arrive as strings) or nothing at all. null / undefined / '' /
// NaN are 0 — never the text "NaN" (and -0 is 0, never "-0.00").
const toAmount = (amount) => {
  const n = typeof amount === 'number' ? amount : parseFloat(amount);
  return Number.isFinite(n) ? n + 0 : 0;
};

// A value that ROUNDS to zero at the precision it is printed with is printed as
// zero, without a sign (operator 2026-10-14: settled customers showed
// "−RM 0.00" — a −0.0000000000582 float residue went through Intl / toFixed
// as a negative zero). |n| below half the last printed digit → 0.
const zeroAtDp = (n, dp) => (Math.abs(n) < 0.5 * Math.pow(10, -dp) ? 0 : n);

/**
 * ONE "is this customer account ACTIVE?" rule — the mirror of the backend's
 * lib/customer-accounts.js (the API also sends `is_active` on the Fiat /
 * Crypto Balance customer rows). Operator 2026-10-14:
 *   - MYR and the stablecoins (USDT, USDC, FDUSD): active from 0.005 — anything
 *     smaller prints as 0.00 and is a rounding residue (the server clears those
 *     every 10 minutes as "Rounding residue" in Expenses & Profit);
 *   - every other coin (BTC, ETH, …): active whenever non-zero after rounding
 *     to 8 dp — a coin balance is never residue, it may be worth a lot.
 * Positive = the customer owes us; negative = we owe the customer.
 */
export const CUSTOMER_RESIDUE_FLOOR = 0.005;
const RESIDUE_FLOOR_CURRENCIES = new Set(['MYR', 'USDT', 'USDC', 'FDUSD']);
export const isActiveCustomerBalance = (balance, currency = 'MYR') => {
  const b = parseFloat(toAmount(balance).toFixed(8));
  const c = String(currency || 'MYR').toUpperCase();
  return RESIDUE_FLOOR_CURRENCIES.has(c) ? Math.abs(b) >= CUSTOMER_RESIDUE_FLOOR : b !== 0;
};

/**
 * Format a MYR (or other ISO-currency) amount — THE one money formatter for the
 * whole app (audit D-21: thirteen local copies disagreed on null handling —
 * "0.00", "RM NaN", "RM 0.00" — and on the symbol).
 * A missing / non-numeric amount reads as zero WITH the symbol ("RM 0.00").
 * @param {number|string} amount - The amount to format
 * @param {string} currency - ISO 4217 code (default: 'MYR'; must be a 3-letter code)
 * @returns {string} Formatted currency string, e.g. "RM 1,234.50"
 */
export const formatCurrency = (amount, currency = 'MYR') => currencyFormatter(currency).format(zeroAtDp(toAmount(amount), 2));

// Display formatter for crypto amounts (see formatCryptoAmount).
const cryptoDisplayFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 8
});

/**
 * Format a crypto amount for DISPLAY: at least 2, at most 8 decimals, float
 * noise and trailing zeros dropped, thousands grouped — so 5141.7103000000006
 * prints "5,141.7103", 0.00000282 prints "0.00000282" (never rounded away to
 * "0.00") and 1.5 prints "1.50". Replaces the per-screen precision (raw / 8 dp /
 * 4 dp / 2 dp for the same order, audit D-21).
 *
 * A missing / non-numeric amount reads as zero, like formatCurrency.
 * @param {number|string} amount
 * @returns {string} e.g. "1,234.50"
 */
export const formatCryptoAmount = (amount) => cryptoDisplayFormatter.format(zeroAtDp(toAmount(amount), 8));

/**
 * The Crypto Balance page's FIXED-width balance format (operator 2026-05-14:
 * stablecoins exactly 2 dp, every other coin exactly 8 dp, no grouping) — so a
 * column of balances lines up. Everything else uses formatCryptoAmount.
 * @param {number|string} amount
 * @param {string} code - coin code (decides 2 vs 8 dp via maxInputDecimals)
 */
export const formatCryptoFixed = (amount, code) => {
  const dp = maxInputDecimals(code);
  return zeroAtDp(toAmount(amount), dp).toFixed(dp);
};

/**
 * Format an amount in ANY currency or coin, in its own unit: MYR (or no code)
 * is the app's "RM 1,234.50" (formatCurrency); anything else is the number then
 * the code, "1,234.50 USDT" (formatCryptoAmount: 2 to 8 decimals, so a
 * 6-decimal stablecoin amount is never rounded away). Intl refuses non-ISO codes
 * such as USDT, so the symbol form is MYR-only. Three screens had their own
 * copy of this (Transaction History breakdown, Money Available transfer lines,
 * the manual-transaction confirm), and the Expenses page printed MYR as
 * "MYR 1,234.00" and every other currency code-first.
 * @param {number|string} amount
 * @param {string} [code] - currency / coin code (default 'MYR')
 */
export const formatAmount = (amount, code = 'MYR') => {
  const c = String(code || 'MYR').toUpperCase();
  if (c === 'MYR') return formatCurrency(amount, 'MYR');
  return `${formatCryptoAmount(amount)} ${c}`;
};

/**
 * The wording of a customer's balance — ONE vocabulary (audit D-21 found four:
 * "owes us"/"credit", "We owe this customer", "Customer has credit", "Has
 * credit"). Sign convention (since 2026-05-01): positive = the customer owes
 * us; negative = the customer holds credit with us; zero = settled. An account
 * that is not ACTIVE (isActiveCustomerBalance — a sub-0.005 MYR / stablecoin
 * residue) is settled too, so it never reads "owes us" next to "RM 0.00".
 * @param {number|string} value - the customer's balance in one currency
 * @param {string} [currency] - that currency (default 'MYR')
 * @returns {{ sign: 'owes'|'credit'|'settled', label: string }}
 */
export const customerBalanceSign = (value, currency = 'MYR') => {
  const v = toAmount(value);
  if (!isActiveCustomerBalance(v, currency)) return { sign: 'settled', label: 'Settled' };
  if (v > 0) return { sign: 'owes', label: 'Customer owes us' };
  return { sign: 'credit', label: 'Customer has credit' };
};

/**
 * Parse a stored timestamp value into a Date, treating anything without
 * explicit timezone info as UTC — the single source of truth for reading
 * back what the backend wrote. Handles:
 *   - SQLite naive strings "YYYY-MM-DD HH:MM:SS" / "YYYY-MM-DDTHH:MM:SS" (→ UTC)
 *   - ISO strings that already carry a timezone (Z / ±HH:MM / ±HHMM)
 *   - epoch numbers, seconds or milliseconds (values < 1e12 are seconds)
 *   - Date instances (passed through)
 * Use this (not `new Date(...)`) anywhere a stored value is compared or
 * sorted, so ordering matches what formatDate/formatTime render.
 * @param {string|number|Date} value
 * @returns {Date} parsed Date (Invalid Date if unparseable/empty)
 */
export const parseStoredDate = (value) => {
  if (value === null || value === undefined || value === '') return new Date(NaN);
  if (value instanceof Date) return value;
  if (typeof value === 'number') {
    return new Date(value < 1e12 ? value * 1000 : value);
  }
  const str = String(value);
  const hasTimezone = str.includes('Z') ||
                     /[+-]\d{2}:\d{2}$/.test(str) ||
                     /[+-]\d{4}$/.test(str);
  if (!hasTimezone && !str.includes('T')) {
    // SQLite format: "YYYY-MM-DD HH:MM:SS" - treat as UTC
    return new Date(str.replace(' ', 'T') + 'Z');
  } else if (!hasTimezone) {
    // Has T but no timezone - treat as UTC
    return new Date(str + 'Z');
  }
  return new Date(str);
};

/**
 * Format date in UTC+8 (Asia/Kuala_Lumpur) timezone
 * This ensures consistent display regardless of user's system timezone
 * @param {string|number|Date} date - Date to format
 * @param {boolean} includeTime - Whether to include time (default: true)
 * @returns {string} Formatted date string in UTC+8
 */
export const formatDate = (date, includeTime = true) => {
  if (!date) return '';

  const dateObj = parseStoredDate(date);

  // Check for invalid date
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: MYT
  };

  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
    options.hour12 = true;
  }

  return new Intl.DateTimeFormat('en-MY', options).format(dateObj);
};

/**
 * Format date for display (shorter format, date only) in UTC+8
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string in UTC+8
 */
export const formatDateShort = (date) => {
  return formatDate(date, false);
};

/**
 * Format date with relative time (e.g., "2 hours ago") in UTC+8
 * @param {string|number|Date} date - Date to format
 * @returns {string} Relative time string
 */
export const formatDateRelative = (date) => {
  if (!date) return '';

  const dateObj = parseStoredDate(date);
  if (isNaN(dateObj.getTime())) return '';

  const now = new Date();
  const diffMs = now - dateObj;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return formatDate(dateObj, true);
};

/**
 * Format time only (no date) in UTC+8 (Asia/Kuala_Lumpur).
 * @param {string|number|Date} date - Date to format
 * @param {Object} [opts]
 * @param {boolean} [opts.seconds=false] - Include seconds
 * @param {boolean} [opts.hour12=true] - 12-hour clock with AM/PM (false = 24h)
 * @returns {string} Formatted time string in UTC+8
 */
export const formatTime = (date, { seconds = false, hour12 = true } = {}) => {
  if (!date) return '';

  const dateObj = parseStoredDate(date);
  if (isNaN(dateObj.getTime())) return '';

  const options = {
    hour: '2-digit',
    minute: '2-digit',
    hour12,
    timeZone: MYT
  };
  if (seconds) options.second = '2-digit';

  return new Intl.DateTimeFormat('en-MY', options).format(dateObj);
};

/**
 * Today's calendar date in Malaysia (UTC+8) as "YYYY-MM-DD", independent of
 * the browser's own timezone. Matches how the backend buckets every figure —
 * use this instead of `new Date().toISOString().slice(0, 10)` (which is
 * today's UTC date, and drifts a day around the UTC+8 midnight boundary).
 * @returns {string} "YYYY-MM-DD" in Malaysia time
 */
export const todayMYT = () => new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);

/**
 * Interpret a `<input type="datetime-local">` value (or the equivalent
 * "YYYY-MM-DD HH:MM[:SS]" text) as Malaysia wall-clock time — FIXED +08:00,
 * never the browser's own timezone — and convert it to a UTC SQLite
 * datetime string "YYYY-MM-DD HH:MM:SS" ready to send to the backend.
 * Inverse of {@link utcSqlToKlInput}.
 * @param {string} datetimeLocalValue - "YYYY-MM-DDTHH:MM[:SS]" or "YYYY-MM-DD HH:MM[:SS]"
 * @returns {string|null} UTC SQL datetime string, or null if unparseable
 */
export const klInputToUtcSql = (datetimeLocalValue) => {
  if (!datetimeLocalValue) return null;
  const normalized = String(datetimeLocalValue).trim().replace(' ', 'T');
  const withSeconds = /T\d{2}:\d{2}$/.test(normalized) ? `${normalized}:00` : normalized;
  const utcDate = new Date(`${withSeconds}+08:00`);
  if (isNaN(utcDate.getTime())) return null;

  const pad = (n) => String(n).padStart(2, '0');
  return `${utcDate.getUTCFullYear()}-${pad(utcDate.getUTCMonth() + 1)}-${pad(utcDate.getUTCDate())} ` +
    `${pad(utcDate.getUTCHours())}:${pad(utcDate.getUTCMinutes())}:${pad(utcDate.getUTCSeconds())}`;
};

/**
 * Convert a stored value (UTC-naive SQL string, ISO string, epoch, or Date —
 * anything {@link parseStoredDate} accepts) into a Malaysia wall-clock
 * "YYYY-MM-DDTHH:MM" string suitable for prefilling a
 * `<input type="datetime-local">`. Inverse of {@link klInputToUtcSql}.
 * @param {string|number|Date} storedValue
 * @returns {string} "YYYY-MM-DDTHH:MM" in Malaysia time, or '' if unparseable
 */
export const utcSqlToKlInput = (storedValue) => {
  const dateObj = parseStoredDate(storedValue);
  if (isNaN(dateObj.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: MYT,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(dateObj).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

/**
 * Truncate text to specified length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text with ellipsis
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Default compression options for image uploads
 */
export const DEFAULT_COMPRESSION_OPTIONS = {
  maxWidth: 1920,           // Maximum width in pixels
  maxHeight: 1920,          // Maximum height in pixels
  quality: 0.8,             // JPEG quality (0-1)
  mimeType: 'image/jpeg',   // Output format
  maxSizeMB: 1,             // Target max file size in MB
  preserveExif: false       // Don't preserve EXIF data for privacy
};

/**
 * Higher-quality preset for DOCUMENTS with fine detail — KYC IDs, invoices.
 * A bigger dimension + size ceiling keeps small text (ID numbers, line items)
 * legible, while still shrinking a raw phone photo by an order of magnitude.
 * Use DEFAULT_COMPRESSION_OPTIONS for receipts/proofs/screenshots.
 */
export const DOCUMENT_COMPRESSION_OPTIONS = {
  maxWidth: 2560,
  maxHeight: 2560,
  quality: 0.85,
  mimeType: 'image/jpeg',
  maxSizeMB: 2,
  preserveExif: false
};

/**
 * Compress an image file on the client side before uploading
 * Uses Canvas API for browser-native compression
 * 
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Maximum width in pixels (default: 1920)
 * @param {number} options.maxHeight - Maximum height in pixels (default: 1920)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.8)
 * @param {string} options.mimeType - Output mime type (default: 'image/jpeg')
 * @param {number} options.maxSizeMB - Target max file size in MB (default: 1)
 * @returns {Promise<{file: File, stats: Object}>} Compressed file and compression stats
 */
export const compressImage = async (file, options = {}) => {
  const opts = { ...DEFAULT_COMPRESSION_OPTIONS, ...options };
  
  // Skip compression for non-image files
  if (!file.type.startsWith('image/')) {
    return {
      file,
      stats: {
        originalSize: file.size,
        compressedSize: file.size,
        compressionRatio: 1,
        wasCompressed: false,
        reason: 'Not an image file'
      }
    };
  }

  // Skip compression for very small files (< 100KB)
  if (file.size < 100 * 1024) {
    return {
      file,
      stats: {
        originalSize: file.size,
        compressedSize: file.size,
        compressionRatio: 1,
        wasCompressed: false,
        reason: 'File already small enough'
      }
    };
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      try {
        // Calculate new dimensions while preserving aspect ratio
        let { width, height } = img;
        const aspectRatio = width / height;

        if (width > opts.maxWidth) {
          width = opts.maxWidth;
          height = width / aspectRatio;
        }

        if (height > opts.maxHeight) {
          height = opts.maxHeight;
          width = height * aspectRatio;
        }

        // Round dimensions to whole numbers
        width = Math.round(width);
        height = Math.round(height);

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw image with white background (for transparency)
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        // Try to compress to target size with iterative quality reduction
        let quality = opts.quality;
        const targetSize = opts.maxSizeMB * 1024 * 1024;
        const minQuality = 0.3;

        const attemptCompression = (currentQuality) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              // If still too large and quality can be reduced further
              if (blob.size > targetSize && currentQuality > minQuality) {
                const newQuality = Math.max(currentQuality - 0.1, minQuality);
                attemptCompression(newQuality);
                return;
              }

              // Create compressed file
              const compressedFile = new File(
                [blob],
                file.name.replace(/\.[^.]+$/, '.jpg'),
                { type: opts.mimeType }
              );

              const stats = {
                originalSize: file.size,
                compressedSize: compressedFile.size,
                compressionRatio: file.size / compressedFile.size,
                savedBytes: file.size - compressedFile.size,
                savedPercentage: Math.round((1 - compressedFile.size / file.size) * 100),
                originalDimensions: { width: img.width, height: img.height },
                newDimensions: { width, height },
                wasCompressed: true,
                finalQuality: currentQuality
              };

              // URL cleanup
              URL.revokeObjectURL(img.src);

              resolve({ file: compressedFile, stats });
            },
            opts.mimeType,
            currentQuality
          );
        };

        attemptCompression(quality);
      } catch (error) {
        URL.revokeObjectURL(img.src);
        reject(error);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image for compression'));
    };

    // Load the image
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Validate image file before compression
 * @param {File} file - File to validate
 * @param {Object} options - Validation options
 * @param {number} options.maxSizeMB - Maximum allowed file size in MB
 * @param {string[]} options.allowedTypes - Allowed mime types
 * @returns {{valid: boolean, error: string|null}}
 */
export const validateImageFile = (file, options = {}) => {
  const {
    maxSizeMB = 10,
    allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
  } = options;

  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'Please select an image file' };
  }

  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: `File type not supported. Allowed: ${allowedTypes.join(', ')}` };
  }

  if (file.size > maxSizeMB * 1024 * 1024) {
    return { valid: false, error: `File size exceeds ${maxSizeMB}MB limit` };
  }

  return { valid: true, error: null };
};
